import { ColumnType } from "src/types/visual";

export const ARRAY_ITEM_SEPARATOR = "[].";

export type ArrayItemColumn = {
  arrayPath: string;
  relativePath: string;
};

export type ObjectArrayItemField = {
  id: string;
  path: string;
  type: string;
};

type ColumnRow = {
  id: string;
  json_path: string;
  alias: string | null;
  type: string;
  isJoinKey: boolean;
  isTimestampColumn: boolean;
  itemFields?: ObjectArrayItemField[];
};

export function splitArrayItemColumnName(name: string): ArrayItemColumn | null {
  const index = name.indexOf(ARRAY_ITEM_SEPARATOR);
  if (index <= 0) {
    return null;
  }
  const relativePath = name.slice(index + ARRAY_ITEM_SEPARATOR.length);
  if (!relativePath || relativePath.includes(ARRAY_ITEM_SEPARATOR)) {
    return null;
  }
  return { arrayPath: name.slice(0, index), relativePath };
}

export function objectArrayParentNames(
  columns: ReadonlyArray<{ name: string; type: string }>,
): Set<string> {
  const names = new Set<string>();
  for (const column of columns) {
    if (column.type === ColumnType.JsonArrayObjects) {
      names.add(column.name);
    }
  }
  return names;
}

export function objectArrayParentNamesByModel(
  columns: ReadonlyArray<{ modelId: string; name: string; type: string }>,
): Map<string, Set<string>> {
  const columnsByModel = new Map<string, { name: string; type: string }[]>();
  for (const column of columns) {
    const modelColumns = columnsByModel.get(column.modelId) ?? [];
    modelColumns.push({ name: column.name, type: column.type });
    columnsByModel.set(column.modelId, modelColumns);
  }
  return new Map(
    Array.from(columnsByModel, ([modelId, modelColumns]) => [
      modelId,
      objectArrayParentNames(modelColumns),
    ]),
  );
}

export function isObjectArrayItemColumn(
  name: string,
  parentNames: ReadonlySet<string> | undefined,
): boolean {
  if (!parentNames) {
    return false;
  }
  const split = splitArrayItemColumnName(name);
  return split != null && parentNames.has(split.arrayPath);
}

export function nestObjectArrayColumns<T extends ColumnRow>(
  columns: T[],
): (T & { itemFields: ObjectArrayItemField[] })[] {
  const parentNames = objectArrayParentNames(
    columns.map((column) => ({ name: column.json_path, type: column.type })),
  );
  const itemsByParent = new Map<string, ObjectArrayItemField[]>();
  const rows: T[] = [];

  for (const column of columns) {
    const split = splitArrayItemColumnName(column.json_path);
    if (!split) {
      rows.push({ ...column, itemFields: column.itemFields ?? [] });
      continue;
    }
    if (!isObjectArrayItemColumn(column.json_path, parentNames)) {
      continue;
    }
    const fields = itemsByParent.get(split.arrayPath) ?? [];
    fields.push({ id: column.id, path: split.relativePath, type: column.type });
    itemsByParent.set(split.arrayPath, fields);
  }

  const nested = rows.map((column) => {
    if (!parentNames.has(column.json_path)) {
      return column;
    }
    return { ...column, itemFields: itemsByParent.get(column.json_path) ?? [] };
  });

  const orphans = columns.filter(
    (column) =>
      splitArrayItemColumnName(column.json_path) != null &&
      !isObjectArrayItemColumn(column.json_path, parentNames),
  );

  return [
    ...nested,
    ...orphans.map((column) => ({ ...column, itemFields: [] })),
  ] as (T & { itemFields: ObjectArrayItemField[] })[];
}

export function flattenObjectArrayColumns<T extends ColumnRow>(
  columns: T[],
): T[] {
  return columns.flatMap((column) => {
    const parent = { ...column, itemFields: [] };
    if (column.type !== ColumnType.JsonArrayObjects) {
      return [parent];
    }
    const items = (column.itemFields ?? []).map(
      (item) =>
        ({
          ...parent,
          id: item.id,
          json_path: `${column.json_path}${ARRAY_ITEM_SEPARATOR}${item.path}`,
          alias: null,
          type: item.type,
          isJoinKey: false,
          isTimestampColumn: false,
          itemFields: [],
        }) as T,
    );
    return [parent, ...items];
  });
}
